// Pick up a RingCentral call in the app (Nic 2026-11-02, backlog 136): the
// pure pieces behind the card, kept out of the provider so they can be
// tested without a phone.
//
// A rep on a call they answered or placed in the RingCentral app sees a
// card: who is on the line (the server matched the number), and "Pick it
// up here". The switch is a SIP INVITE with a Replaces header, the same
// device-switch the official RingCentral apps do; the tags come from the
// person's own presence read (Read Presence on the phone app). Once the
// call is in the browser, THEIR lead or client opens as if clicked;
// someone else's gets one Direct hot transfer to the owner, never a page
// they cannot see.

// Kill switch, like WARM_TRANSFER_ENABLED: flip off without a revert.
export const PICKUP_ENABLED = true;
// How often the provider asks the server "am I on a call outside the app"
// while idle and visible.
export const PICKUP_POLL_MS = 10 * 1000;
// Every switch attempt, together, gets this long. An INVITE that answers
// after it has still switched the call (the phone app's leg is gone), so
// it is kept and routed like an on-time one, never hung up.
export const SWITCH_CEILING_MS = 12 * 1000;
// Everything before the INVITE (the adopt, the microphone prompt, the
// presence read, the phone registration) gets this long in all, with a
// Cancel on the card; past it the card goes back to the offer.
export const PICKUP_DEADLINE_MS = 30 * 1000;
// How long the Sales board gets to claim an adopted call before the card
// falls back to the on-the-line view.
export const PICKUP_CLAIM_MS = 15 * 1000;

export const COPY = {
  pickUp: 'Pick it up here',
  leaveIt: 'Leave it on my phone',
  switching: 'Bringing the call here...',
  cancel: 'Cancel',
  slow: 'Picking up took too long. It stays on your phone.',
  transferTo: (first) => `Hot transfer to ${first}`,
  mute: 'Mute',
  unmute: 'Unmute',
  hangUp: 'Hang up',
  ended: 'The call ended.',
  failed: 'Could not bring the call here. It stays on your phone.',
  handed: (first) => `${first} has them now.`,
  mic: 'Microphone permission is required to pick up the call.',
  login: 'Log into RingCentral first (the chip in the board header).',
  unclaimed: 'Open them from the board to work the call.',
  busyLine: (first) => `${first} is on a call. Try again in a moment.`,
  notYet: 'They have not picked up yet.',
  relogin:
    'Sign out of RingCentral in the app and back in once, then the call can be picked up.',
};

const firstOf = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)[0] || '';

// "You're on a call with Michael Stephens, Josue's client" and the
// on-the-line heading, from the server's match.
export const pickupCopy = (match, { number = null } = {}) => {
  const name =
    match?.name || (number ? `(...${String(number).slice(-4)})` : 'someone');
  const owner = match && !match.mine ? firstOf(match.ownerName) : '';
  const whose = owner
    ? match.kind === 'client'
      ? `${owner}'s client`
      : `${owner}'s lead`
    : '';
  return {
    offer: whose
      ? `You're on a call with ${name}, ${whose}`
      : `You're on a call with ${name}`,
    online: whose
      ? `On the line with ${name} (${whose})`
      : `On the line with ${name}`,
    transfer: owner ? COPY.transferTo(owner) : null,
    ownerFirst: owner,
  };
};

// Whether the card should show at all.
export const shouldOfferPickup = ({
  call,
  match,
  ownSip = null,
  dismissedId = null,
  hasCall = false,
  testCall = null,
  orphan = false,
}) => {
  if (!PICKUP_ENABLED || !call?.sessionId || !match) return false;
  if (hasCall || orphan) return false;
  if (testCall === 'live' || testCall === 'dialing') return false;
  if (
    ownSip?.telephonySessionId &&
    ownSip.telephonySessionId === call.sessionId
  )
    return false;
  if (dismissedId && dismissedId === call.sessionId) return false;
  return true;
};

// RingCentral's own tags look like 10.14.20.199-5070-63fbd8da20314c; the
// server's tag belongs in to-tag on the first attempt.
const SERVER_TAG = /^\d+\.\d+\.\d+\.\d+-\d+-/;
export const tagOrder = (sip) => {
  const to = String(sip?.toTag || '');
  const from = String(sip?.fromTag || '');
  if (SERVER_TAG.test(to) && !SERVER_TAG.test(from))
    return { toTag: to, fromTag: from };
  if (SERVER_TAG.test(from) && !SERVER_TAG.test(to))
    return { toTag: from, fromTag: to };
  return { toTag: to, fromTag: from };
};

export const replacesHeader = (
  sip,
  { swapped = false, earlyOnly = true } = {},
) => {
  const first = tagOrder(sip);
  const toTag = swapped ? first.fromTag : first.toTag;
  const fromTag = swapped ? first.toTag : first.fromTag;
  return `${sip.telephonySessionId};to-tag=${toTag};from-tag=${fromTag}${earlyOnly ? ';early-only' : ''}`;
};

// The retry matrix: attempt 1 as the recipe; a 486 means "do not replace a
// confirmed dialog" (early-only), so try without it; anything else means
// the tags were backwards. At most three INVITEs.
export const nextSwitchAttempt = (subject, attempt) => {
  const code = String(subject || '').match(/\b(\d{3})\b/)?.[1] || '';
  if (attempt === 1) {
    return code === '486'
      ? { swapped: false, earlyOnly: false }
      : { swapped: true, earlyOnly: true };
  }
  if (attempt === 2) {
    return code === '486' ? { swapped: true, earlyOnly: false } : null;
  }
  return null;
};

// An outbound call is only real once the far end picked up: RingCentral
// answers the caller's own leg the moment they dial, so the server cannot
// tell, but the person's own presence row can (telephonyStatus). A row
// RingCentral has not written yet reads as not connected.
export const isConnectedRow = (row) =>
  !!row && String(row.telephonyStatus || '') === 'CallConnected';

// The presence row for the span the server named.
export const activeCallFor = (presenceBody, sessionId) =>
  (presenceBody?.activeCalls || []).find(
    (c) =>
      String(c?.telephonySessionId || c?.sessionId || '') === String(sessionId),
  ) || null;
